import React from 'react';
import { adminAnalyticsAPI } from '../../../services/api';
import useSalesRange from './useSalesRange';
import useSalesQuery from './useSalesQuery';
import DateRangeControl from './DateRangeControl';
import { Bone, EmptyState, ErrorState, InlineError, RefetchBar } from './SalesStates';
import { formatCount, formatCurrency, formatPercent, formatRangeLabel, netCents, netSalesCount } from './salesUtils';
import { REFUND_BADGE, REFUND_TEXT } from './theme';

const fetchSales = (params, opts) => adminAnalyticsAPI.sales(params, opts);

// Same card, row height and column widths as the loaded list.
const ListSkeleton = () => (
  <div className="sales-card space-y-1" data-testid="sales-by-video-skeleton" aria-busy="true" aria-label="Loading sales by video">
    <div className="flex justify-between mb-3"><Bone className="h-4 w-36" /><Bone className="h-4 w-28" /></div>
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className="flex items-center gap-3 sm:gap-4 px-3 py-3">
        <Bone className="w-8 h-5" />
        <Bone className="hidden sm:block w-20 aspect-video" />
        <div className="flex-1 space-y-2.5"><Bone className="h-4 w-1/2" /><Bone className="h-1.5 w-full" /></div>
        <div className="w-24 sm:w-32 space-y-2 flex flex-col items-end"><Bone className="h-5 w-14" /><Bone className="h-3 w-24" /></div>
      </div>
    ))}
  </div>
);

const Chevron = () => (
  <svg className="sales-row-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const REFUND_BADGE_LABEL = { partial: 'Partial refund', full: 'Refunded' };

// Shown only for rows with money refunded in range; the badge only for partial / full.
const RefundLine = ({ row, currency }) => {
  const refunded = row.refunded_cents || 0;
  const badge = REFUND_BADGE_LABEL[row.refund_status];
  if (refunded <= 0 && !badge) return null;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs">
      {badge && (
        <span className={REFUND_BADGE} data-testid={`refund-badge-${row.video_id}`}>{badge}</span>
      )}{' '}
      <span className={`sales-num ${REFUND_TEXT}`}>Refunds {formatCurrency(refunded, currency)}</span>
    </span>
  );
};

const VideoShareRow = ({ row, currency, onSelect }) => (
  <li>
    <button type="button" onClick={() => onSelect(row.video_id)} className="sales-row" data-testid={`by-video-row-${row.video_id}`}>
      <span className={`sales-rank ${row.rank <= 3 ? 'sales-rank-top' : ''}`}>#{row.rank}</span>
      {row.thumbnail_url ? (
        <img src={row.thumbnail_url} alt="" className="sales-thumb hidden sm:block w-20" />
      ) : (
        <span className="sales-thumb hidden sm:block w-20" aria-hidden="true" />
      )}
      <span className="flex-1 min-w-0">
        <span className="block text-gray-100 font-medium truncate">{row.title}</span>
        <span className="sales-track mt-2.5 block">
          <span className="sales-fill block" style={{ width: `${row.pct_of_revenue}%` }} data-testid={`share-bar-${row.video_id}`} />
        </span>
        <RefundLine row={row} currency={currency} />
      </span>
      <span className="text-right shrink-0 w-24 sm:w-32">
        <span className="block font-display font-semibold text-lg leading-tight text-brand-yellow sales-num">{formatPercent(row.pct_of_revenue)}</span>
        <span className="block text-xs text-gray-500 sales-num truncate" title={`Net ${formatCurrency(netCents(row), currency)}, ${formatCount(netSalesCount(row))} sales`}>
          net {formatCurrency(netCents(row), currency)} &middot; {formatCount(netSalesCount(row))} sales
        </span>
      </span>
      <Chevron />
    </button>
  </li>
);

// "Analytics by video": every video's share of NET sales in a range (refunds count
// against the original sale); pick one to drill in.
const SalesByVideoView = ({ onSelectVideo, onBack }) => {
  const { range, params, setRange } = useSalesRange();
  const { data, error, loading, retry } = useSalesQuery(fetchSales, params);
  const meta = data?.meta;
  const currency = meta?.currency || 'usd';
  const noSales = data?.videos_with_no_sales_count || 0;

  let body;
  if (!data && error) {
    body = <ErrorState message="Could not load sales by video." onRetry={retry} testId="sales-by-video-error" />;
  } else if (!data) {
    body = <ListSkeleton />;
  } else if (!data.by_video.length) {
    body = (
      <EmptyState title="No sales in this range" testId="sales-by-video-empty">
        Nothing sold between these dates. Try a wider range or All time.
      </EmptyState>
    );
  } else {
    body = (
      <div className="space-y-3 sales-enter">
        {error && <InlineError onRetry={retry} />}
        <div className="sales-card sales-card-lit">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <h3 className="sales-heading">Share of revenue</h3>
            <span className="flex flex-col items-end text-right sales-num">
              <span className="text-sm text-gray-400">
                {formatCount(netSalesCount(data.totals))} sales &middot; {formatCurrency(netCents(data.totals), currency)} net
              </span>{' '}
              {(data.totals.refunded_cents || 0) > 0 && (
                <span className="text-xs text-gray-500">
                  gross {formatCurrency(data.totals.gross_cents, currency)},{' '}
                  <span className={REFUND_TEXT}>refunds {formatCurrency(data.totals.refunded_cents, currency)}</span>
                </span>
              )}
            </span>
          </div>
          <ul className="space-y-0.5 -mx-3">
            {data.by_video.map((row) => (
              <VideoShareRow key={row.video_id} row={row} currency={currency} onSelect={onSelectVideo} />
            ))}
          </ul>
          {noSales > 0 && (
            <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-white/5">
              {noSales} video{noSales === 1 ? '' : 's'} with no sales in this range.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <section className="relative space-y-4 sm:space-y-5 min-w-0" data-testid="sales-by-video" aria-label="Analytics by video">
      {loading && data && <RefetchBar />}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
        <button type="button" onClick={onBack} className="sales-btn sales-btn-ghost self-start sm:self-center -ml-2 sm:ml-0" data-testid="sales-by-video-back">
          <span aria-hidden="true">&larr;</span> Statistics
        </button>
        <div className="min-w-0">
          <h2 className="sales-title">Analytics by video</h2>
          <p className="text-xs text-gray-400 mt-1 sales-num">{formatRangeLabel(meta?.from || params.from, meta?.to || params.to)}</p>
        </div>
      </div>
      <DateRangeControl
        range={range}
        resolved={{ from: params.from, to: params.to }}
        onChange={setRange}
        currencies={meta?.currencies}
        currency={currency}
        onCurrencyChange={(c) => setRange({ currency: c })}
      />
      {body}
    </section>
  );
};

export default SalesByVideoView;
